import React, { useEffect, useRef, useState } from 'react';
import { Receipt, User, Users as UsersIcon, Loader2, Building2, Paperclip, X } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { BoutonTesterEmail } from '../ui/TesterEmailModal';
import { SelecteurVariables } from '../ui/SelecteurVariables';
import { EditeurMessage } from '../ui/EditeurMessage';
import { MessageFormatBar, wrapNotificationSelection } from '../ui/MessageFormatBar';
import { LigneNotification, ModeNotificationsContext, FournisseurEnregistrement, EtatEnregistrement, GroupeNotifications } from '../ui/LigneNotification';
import { Segmented } from '../ui/Field';
// Ré-export : une vingtaine d'écrans importent `wrapNotificationSelection` depuis ce fichier, où elle
// est née. Elle vit maintenant dans le socle, avec la barre de mise en forme qui s'en sert.
export { wrapNotificationSelection };

export interface NotificationAttachment { name: string; data: string; }

const MAX_NOTIFICATION_ATTACHMENT_BYTES = 4 * 1024 * 1024;

// Même logique que les autres pièces jointes de l'app (modèles de Démarche client) : une image est
// recompressée côté navigateur pour éviter un base64 énorme, un PDF est simplement plafonné en taille.
const compressNotificationImage = (file: File, maxDim = 1200, quality = 0.8): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Lecture du fichier impossible'));
    reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('Image invalide'));
        img.onload = () => {
            const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
            const w = Math.max(1, Math.round(img.width * scale));
            const h = Math.max(1, Math.round(img.height * scale));
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) { reject(new Error('Canvas non supporté')); return; }
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
});
const readNotificationFileAsDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('Lecture du fichier impossible'));
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
});

/** Bouton compact "joindre un fichier" (PDF/image) réutilisé dans chaque bloc d'email — un seul fichier par
 *  type d'email, envoyé en pièce jointe à chaque envoi de cet email. */
export const NotificationAttachmentField: React.FC<{
    attachment: NotificationAttachment | null;
    onChange: (att: NotificationAttachment | null) => void;
}> = ({ attachment, onChange }) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setError('');
        setUploading(true);
        const task = file.type.startsWith('image/')
            ? compressNotificationImage(file)
            : (file.size > MAX_NOTIFICATION_ATTACHMENT_BYTES
                ? Promise.reject(new Error(`Ce fichier dépasse ${Math.round(MAX_NOTIFICATION_ATTACHMENT_BYTES / (1024 * 1024))} Mo — merci d'en choisir un plus léger.`))
                : readNotificationFileAsDataUrl(file));
        task
            .then((dataUrl) => onChange({ name: file.name, data: dataUrl }))
            .catch((err) => setError(err?.message || 'Impossible de traiter ce fichier.'))
            .finally(() => setUploading(false));
    };

    return (
        <div className="space-y-1">
            <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-surface-soft text-content-muted hover:text-content text-[11px] font-bold cursor-pointer transition-all">
                    {uploading ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />}
                    {uploading ? 'Chargement...' : (attachment ? attachment.name : 'Joindre un fichier (PDF/image)')}
                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFileChange} disabled={uploading} />
                </label>
                {attachment && (
                    <button type="button" onClick={() => onChange(null)} title="Retirer la pièce jointe"
                        className="p-1 rounded-md text-content-muted hover:text-red-400 transition-all"><X size={12} /></button>
                )}
            </div>
            {error && <p className="text-[11px] text-red-400 font-semibold">{error}</p>}
        </div>
    );
};

interface NotificationToken { token: string; label: string; }
// Toutes les variables disponibles, dans toutes les sections (rappel salarié/client, assignation, confirmation,
// facture) — certaines n'ont de sens que dans certains emails, mais toutes sont utilisables partout côté serveur.
// Labels volontairement suffixés (client/entreprise/salarié assigné) pour ne jamais prêter à confusion entre deux
// variables proches (ex: adresse client vs adresse entreprise, numéro client vs numéro entreprise).
export const ALL_NOTIFICATION_TOKENS: NotificationToken[] = [
    { token: '{{client_nom}}', label: 'Nom client' },
    { token: '{{client_telephone}}', label: 'Numéro client' },
    { token: '{{client_email}}', label: 'Email client' },
    { token: '{{adresse}}', label: 'Adresse client' },
    { token: '{{lieu_rdv}}', label: 'Lieu du rendez-vous (domicile/entreprise)' },
    { token: '{{salarie_prenom}}', label: 'Prénom salarié assigné' },
    { token: '{{salarie_nom}}', label: 'Nom salarié assigné' },
    { token: '{{salarie_nom_complet}}', label: 'Nom complet salarié assigné' },
    { token: '{{salarie_telephone}}', label: 'Numéro salarié assigné' },
    { token: '{{salarie_email}}', label: 'Email salarié assigné' },
    { token: '{{salaries}}', label: 'Tous les intervenants' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{entreprise_telephone}}', label: 'Numéro entreprise' },
    { token: '{{entreprise_email}}', label: 'Email entreprise' },
    { token: '{{entreprise_adresse}}', label: 'Adresse entreprise' },
    { token: '{{entreprise_tva}}', label: 'TVA entreprise' },
    { token: '{{entreprise_siret}}', label: 'SIRET entreprise' },
    { token: '{{date}}', label: 'Date du rendez-vous' },
    { token: '{{horaire}}', label: 'Horaire du rendez-vous (plage)' },
    { token: '{{heure}}', label: 'Heure du rendez-vous (début)' },
    { token: '{{prestation}}', label: 'Nom de la prestation' },
    { token: '{{prestation_prix}}', label: 'Prix de la prestation' },
    { token: '{{prestation_duree}}', label: 'Durée de la prestation' },
    { token: '{{statut_paiement}}', label: 'Statut de paiement (résumé)' },
    { token: '{{total}}', label: 'Total facture' },
    { token: '{{lien_paiement}}', label: 'Lien de paiement Stripe (si configuré)' },
    { token: '{{lien_rdv}}', label: 'Bouton "Prendre RDV" (cliquez où l\'insérer)' },
    { token: '{{deja_regle}}', label: 'Montant déjà réglé' },
    { token: '{{reste_a_payer}}', label: 'Reste à payer' },
    { token: '{{moyen_paiement}}', label: 'Moyen de paiement' },
    { token: '{{date_echeance}}', label: 'Date limite de paiement' },
    { token: '{{montant_regle}}', label: 'Montant réglé' },
    { token: '{{motif}}', label: "Motif de l'absence" },
    // Phrase composée depuis Paramètres > Horaires (règle d'annulation). Vide si aucune règle n'y est activée.
    { token: '{{annulation}}', label: "Règle d'annulation" },
    // Où en est la carte de fidélité du client. Vide quand la carte est éteinte ou que ce client
    // n'entre pas dedans : le jeton ne laisse rien derrière lui.
    { token: '{{fidelite}}', label: 'Carte de fidélité du client' },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Variables des deux mails de signature en ligne — jeu réduit : ce sont celles que la route serveur fournit.
const SIGNATURE_TOKENS: NotificationToken[] = [
    { token: '{{prenom}}', label: 'Prénom du client' },
    { token: '{{nom}}', label: 'Nom du client' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{document}}', label: 'Le devis (avec son numéro)' },
    { token: '{{logo}}', label: 'Votre logo' },
];

// Variables des e-mails du démarchage à domicile — celles que fournit le serveur (envoyerEmailApresVisite,
// envoyerConfirmationAnticipation).
const DEMARCHAGE_VISITE_TOKENS: NotificationToken[] = [
    { token: '{{prenom}}', label: 'Prénom du client' },
    { token: '{{nom}}', label: 'Nom du client' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{commercial}}', label: 'Commercial venu chez le client' },
    { token: '{{montant_devis}}', label: 'Montant du devis' },
    { token: '{{bouton}}', label: 'Bouton « Choisir mon rendez-vous »' },
    { token: '{{date_min}}', label: 'Premier rendez-vous possible' },
    { token: '{{date_anticipee}}', label: 'Premier jour sur demande du client' },
    { token: '{{date_paiement}}', label: 'Premier jour où un paiement est possible' },
    { token: '{{fin_retractation}}', label: 'Fin du délai de rétractation' },
    { token: '{{logo}}', label: 'Votre logo' },
];
const DEMARCHAGE_ANTICIPATION_TOKENS: NotificationToken[] = [
    { token: '{{prenom}}', label: 'Prénom du client' },
    { token: '{{nom}}', label: 'Nom du client' },
    { token: '{{entreprise}}', label: 'Nom entreprise' },
    { token: '{{date}}', label: "Date de l'intervention" },
    { token: '{{fin_retractation}}', label: 'Fin du délai de rétractation' },
    { token: '{{date_demande}}', label: 'Date et heure de la demande' },
    { token: '{{texte_demande}}', label: 'Texte accepté par le client' },
    { token: '{{logo}}', label: 'Votre logo' },
];

/** Insère un jeton {{...}} à la position du curseur dans le champ (input/textarea) actuellement ciblé,
 *  au lieu d'obliger l'utilisateur à copier-coller le code à la main. */
export const insertNotificationToken = (
    el: HTMLInputElement | HTMLTextAreaElement | null,
    value: string,
    setValue: (v: string) => void,
    token: string,
) => {
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    setValue(next);
    if (el) {
        requestAnimationFrame(() => {
            // blur puis focus : le navigateur fait défiler le champ jusqu'au curseur, donc jusqu'à
            // la variable qu'on vient de poser, même dans un long message.
            el.blur();
            el.focus();
            const pos = start + token.length;
            el.setSelectionRange(pos, pos);
        });
    }
};

/** Entoure le texte sélectionné (ou insère une paire vide au curseur) avec un marqueur Markdown
 *  (** gras, * italique, __ souligné, [[taille=Npx]]...[[/taille]] taille de police) — interprété
 *  côté serveur par templateBodyToHtml à l'envoi. `closeMarker` par défaut identique à `openMarker`
 *  (cas symétrique gras/italique/souligné) ; distinct pour la taille de police. */

/** Options de taille de police proposées dans le sélecteur du bloc email — valeurs en px interprétées
 *  par templateBodyToHtml via le marqueur [[taille=Npx]]...[[/taille]]. */

/** Un bloc "objet + corps + gras/italique + variables" générique, réutilisé pour chaque email. */
export function NotificationBlock({
    title, toggle, toujours, subject, setSubject, body, setBody, subjectRef, bodyRef, activeField, setActiveField, onInsertToken, tokens,
    attachment, onAttachmentChange, description, extra,
}: {
    title: string;
    /** Envoyé automatiquement, sans interrupteur (obligation légale) : étiquette « Toujours ». */
    toujours?: boolean;
    /** Quand l'email part — affiché sous le titre en mode « en lignes » (Paramètres > Notifications). */
    description?: React.ReactNode;
    /** Réglages propres à cet email (texte d'un bouton, pièce jointe…), placés dans sa fenêtre. */
    extra?: React.ReactNode;
    toggle?: { checked: boolean; onChange: (v: boolean) => void };
    subject: string;
    setSubject: (v: string) => void;
    body: string;
    setBody: (v: string) => void;
    subjectRef: React.RefObject<HTMLInputElement>;
    bodyRef: React.RefObject<HTMLTextAreaElement>;
    activeField: 'subject' | 'body';
    setActiveField: (f: 'subject' | 'body') => void;
    onInsertToken: (token: string) => void;
    /** @deprecated La barre de mise en forme est partagée et agit directement sur `body`.
     *  Les appelants le passent encore ; il sera retiré quand ils seront rouverts. */
    onWrap?: (marker: string) => void;
    tokens?: NotificationToken[];
    attachment?: NotificationAttachment | null;
    onAttachmentChange?: (att: NotificationAttachment | null) => void;
}) {
    const { enLignes } = React.useContext(ModeNotificationsContext);

    // Paramètres > Notifications : une ligne, et le formulaire ci-dessous dans sa fenêtre.
    if (enLignes) {
        return (
            <LigneNotification titre={title} description={description} toggle={toggle} toujours={toujours}
                sujet={subject} corps={body} setSujet={setSubject} setCorps={setBody}
                piece={onAttachmentChange ? attachment : undefined} setPiece={onAttachmentChange}
                extra={extra}
                editeur={
                    <>
                        <label className="block">
                            <span className="block text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mb-1.5">Objet</span>
                            <EditeurMessage uneLigne handleRef={subjectRef} value={subject} onChange={setSubject} tokens={tokens || ALL_NOTIFICATION_TOKENS}
                                onFocus={() => setActiveField('subject')} placeholder="Objet de l'email" ariaLabel="Objet de l'email" />
                        </label>
                        <div>
                            <span className="block text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mb-1.5">Message</span>
                            <MessageFormatBar textareaRef={bodyRef} value={body} onChange={setBody} />
                            <div className="mt-2">
                                <EditeurMessage handleRef={bodyRef} value={body} onChange={setBody} tokens={tokens || ALL_NOTIFICATION_TOKENS} rows={12} choixBoutonLien
                                    onFocus={() => setActiveField('body')} placeholder="Corps de l'email" ariaLabel="Corps de l'email" />
                            </div>
                        </div>
                        {onAttachmentChange && (
                            <NotificationAttachmentField attachment={attachment ?? null} onChange={onAttachmentChange} />
                        )}
                    </>
                }
                // À droite, à côté du texte : on garde le message sous les yeux en insérant.
                variables={<SelecteurVariables integre tokens={tokens || ALL_NOTIFICATION_TOKENS} onInsertToken={onInsertToken} />} />
        );
    }

    return (
        <div className="space-y-3 p-5 rounded-2xl bg-surface border border-border">
            {toggle ? (
                <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm font-bold text-content">{title}</span>
                    <input type="checkbox" checked={toggle.checked} onChange={(e) => toggle.onChange(e.target.checked)} className="accent-brand-600 w-4 h-4" />
                </label>
            ) : (
                <span className="text-sm font-bold text-content">{title}</span>
            )}
            {description && <p className="text-xs text-content-muted italic -mt-1">{description}</p>}
            {extra}
            <EditeurMessage uneLigne handleRef={subjectRef} value={subject} onChange={setSubject} tokens={tokens || ALL_NOTIFICATION_TOKENS}
                onFocus={() => setActiveField('subject')} placeholder="Objet de l'email" ariaLabel="Objet de l'email" />
            <MessageFormatBar textareaRef={bodyRef} value={body} onChange={setBody} />
            <EditeurMessage handleRef={bodyRef} value={body} onChange={setBody} tokens={tokens || ALL_NOTIFICATION_TOKENS} rows={10} choixBoutonLien
                onFocus={() => setActiveField('body')} placeholder="Corps de l'email" ariaLabel="Corps de l'email" />
            {onAttachmentChange && (
                <NotificationAttachmentField attachment={attachment ?? null} onChange={onAttachmentChange} />
            )}
            <SelecteurVariables tokens={tokens || ALL_NOTIFICATION_TOKENS} onInsertToken={onInsertToken} />
            {/* « Tester l'email » posé ICI, une seule fois : ce bloc porte les 58 modèles configurables de
                l'application, répartis sur dix-sept écrans. Le poser sur chaque écran aurait garanti qu'il
                manque quelque part. En bas plutôt qu'en tête, pour ne pas toucher à la ligne titre +
                interrupteur qui est en production sur tous ces écrans. */}
            <div className="flex justify-end pt-0.5">
                <BoutonTesterEmail sujet={subject} corps={body} nomModele={title} />
            </div>
        </div>
    );
}

/**
 * E-mail de FIN DE PRESTATION : envoyé à la clôture, entièrement configurable. L'établissement décide ce qu'il
 * contient : le lien de paiement du solde, le lien d'avis, les deux ou aucun. Les liens se placent avec les
 * variables {{lien_paiement}} et {{lien_avis}} ; l'avis n'envoie JAMAIS un mail à part, c'est un simple lien.
 * Enregistré avec tout le reste par le bouton « Enregistrer » de la page (un seul bouton, pour ne rien perdre).
 */
const FinPrestationEmail: React.FC<{
    actif: boolean; setActif: (v: boolean) => void;
    sujet: string; setSujet: (v: string) => void;
    corps: string; setCorps: (v: string) => void;
    /** true = message envoyé quand la prestation est déjà réglée à la clôture (pas de lien de paiement). */
    paye?: boolean;
    extra?: React.ReactNode;
}> = ({ actif, setActif, sujet, setSujet, corps, setCorps, paye, extra }) => {
    const [champ, setChamp] = useState<'subject' | 'body'>('body');
    const sujetRef = useRef<HTMLInputElement>(null);
    const corpsRef = useRef<HTMLTextAreaElement>(null);
    const inserer = (token: string) => {
        if (champ === 'subject') insertNotificationToken(sujetRef.current, sujet, setSujet, token);
        else insertNotificationToken(corpsRef.current, corps, setCorps, token);
    };
    return (
        <NotificationBlock title={paye ? 'Fin de prestation — déjà réglée' : 'Fin de prestation — reste à payer'}
            description={paye
                ? "Part à la clôture quand le client a déjà tout réglé : pas de lien de paiement, mais la demande d'avis."
                : "Part à la clôture quand il reste un solde : vous y placez le lien de paiement. Une fois payé, le client reçoit « Merci pour votre règlement » et vous êtes prévenu."}
            toggle={{ checked: actif, onChange: setActif }}
            subject={sujet} setSubject={setSujet} body={corps} setBody={setCorps}
            subjectRef={sujetRef} bodyRef={corpsRef} activeField={champ} setActiveField={setChamp}
            onInsertToken={inserer} extra={extra}
            tokens={[
                ...ALL_NOTIFICATION_TOKENS,
                ...(paye ? [] : [{ token: '{{lien_paiement}}', label: 'Bouton « Payer en ligne » (seulement s\'il reste un solde)' }]),
                { token: '{{lien_avis}}', label: 'Bouton « Donner mon avis » (module Avis)' },
            ]} />
    );
};

/** Case « Joindre la facture en PDF » du mail de règlement : s'enregistre tout de suite. */
const JoindreFactureToggle: React.FC<{ champ?: 'invoice_attach_pdf' | 'closure_paid_attach_invoice'; texte?: string }> = ({ champ = 'invoice_attach_pdf', texte }) => {
    const [joindre, setJoindre] = useState(champ === 'invoice_attach_pdf');
    const [erreur, setErreur] = useState('');
    useEffect(() => {
        moduleApi.getFinPrestationEmail().then((r: any) => { if (r && !r.error) setJoindre(champ === 'invoice_attach_pdf' ? r.invoice_attach_pdf !== false : r[champ] === true); }).catch(() => {});
    }, []);
    const changer = async (v: boolean) => {
        const avant = joindre;
        setJoindre(v); setErreur('');
        try {
            const r: any = await moduleApi.saveFinPrestationEmail({ [champ]: v });
            if (r?.error) throw new Error(r.error);
        } catch (e: any) { setJoindre(avant); setErreur(e?.message || "L'enregistrement a échoué."); }
    };
    return (
        <div className="p-4 rounded-2xl bg-surface border border-border space-y-1">
            <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm font-bold text-content flex items-center gap-1.5"><Paperclip size={13} /> Joindre la facture en PDF à ce mail</span>
                <input type="checkbox" checked={joindre} onChange={(e) => changer(e.target.checked)} className="accent-brand-600 w-4 h-4" />
            </label>
            <p className="text-[11px] text-content-muted">{texte || 'Décoché, le mail part sans pièce jointe.'} Ce réglage s'enregistre tout de suite.</p>
            {erreur && <p className="text-xs font-semibold text-red-500">{erreur}</p>}
        </div>
    );
};

/** Onglet "Notifications" de la page Paramètres centrale (visible uniquement pour les clients Prestation) —
 *  personnalisation des emails automatiques du module Planning rendez-vous. */
export const PrestationNotificationsSettings: React.FC<{
    activeModules?: string[];
    /** Destinataire imposé par la page Paramètres (un seul filtre pour tous les écrans de l'onglet). */
    destinataire?: 'client' | 'salarie' | 'etablissement';
}> = ({ activeModules = [], destinataire }) => {
    const showFacture = activeModules.includes('facture');
    // Certains emails ne concernent que des fonctionnalités liées à un module précis — inutile de les
    // proposer si ce module n'est pas actif pour ce client, ça n'arriverait jamais.
    const showDemarches = activeModules.includes('crm');
    const showConges = activeModules.includes('demande_conges');
    const [recipientLocal, setRecipient] = useState<'client' | 'salarie' | 'etablissement'>('client');
    const recipient = destinataire ?? recipientLocal;
    const { enLignes } = React.useContext(ModeNotificationsContext);
    const [echecEnregistrement, setEchecEnregistrement] = useState(false);

    const [notifyEmployee, setNotifyEmployee] = useState(true);
    const [notifyClient, setNotifyClient] = useState(true);
    const [notifyAssignment, setNotifyAssignment] = useState(true);
    const [notifyConfirmation, setNotifyConfirmation] = useState(true);
    const [notifyInvoice, setNotifyInvoice] = useState(true);
    const [employeeSubject, setEmployeeSubject] = useState('');
    const [employeeBody, setEmployeeBody] = useState('');
    const [clientSubject, setClientSubject] = useState('');
    const [clientBody, setClientBody] = useState('');
    const [assignmentSubject, setAssignmentSubject] = useState('');
    const [assignmentBody, setAssignmentBody] = useState('');
    const [confirmationSubject, setConfirmationSubject] = useState('');
    const [confirmationBody, setConfirmationBody] = useState('');
    const [invoicePaidSubject, setInvoicePaidSubject] = useState('');
    const [invoicePaidBody, setInvoicePaidBody] = useState('');
    // E-mail de fin de prestation : enregistré avec le reste par handleSave (route dédiée).
    const [finActif, setFinActif] = useState(true);
    const [finSujet, setFinSujet] = useState('');
    const [finCorps, setFinCorps] = useState('');
    const [finPayeSujet, setFinPayeSujet] = useState('');
    const [finPayeCorps, setFinPayeCorps] = useState('');
    const [invoicePartialSubject, setInvoicePartialSubject] = useState('');
    const [invoicePartialBody, setInvoicePartialBody] = useState('');
    const [invoiceUnpaidSubject, setInvoiceUnpaidSubject] = useState('');
    const [invoiceUnpaidBody, setInvoiceUnpaidBody] = useState('');
    const [bookingLinkSubject, setBookingLinkSubject] = useState('');
    const [bookingLinkBody, setBookingLinkBody] = useState('');
    const [newRequestSubject, setNewRequestSubject] = useState('');
    const [newRequestBody, setNewRequestBody] = useState('');
    const [docResponseSubject, setDocResponseSubject] = useState('');
    const [docResponseBody, setDocResponseBody] = useState('');
    const [docPaymentSubject, setDocPaymentSubject] = useState('');
    const [docPaymentBody, setDocPaymentBody] = useState('');
    const [notifyAbsenceEmployer, setNotifyAbsenceEmployer] = useState(true);
    const [absenceEmployerSubject, setAbsenceEmployerSubject] = useState('');
    const [absenceEmployerBody, setAbsenceEmployerBody] = useState('');
    const [devisSubject, setDevisSubject] = useState('');
    const [devisBody, setDevisBody] = useState('');
    // Une pièce jointe (PDF/image) optionnelle par type d'email — un seul fichier statique envoyé à chaque
    // envoi de cet email, clé = même identifiant que les colonnes ..._subject/..._body (employee, client,
    // assignment, confirmation, invoice_paid/partial/unpaid, booking_link, new_request, doc_response,
    // doc_payment, absence_employer).
    const [attachments, setAttachments] = useState<Record<string, NotificationAttachment | null>>({});
    const setAttachment = (type: string, att: NotificationAttachment | null) => setAttachments((prev) => ({ ...prev, [type]: att }));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    // Champ actuellement ciblé (objet ou corps) par bloc, pour savoir où insérer le prochain clic sur un
    // bouton de variable — par défaut le corps, le plus probable.
    const [employeeActiveField, setEmployeeActiveField] = useState<'subject' | 'body'>('body');
    const [clientActiveField, setClientActiveField] = useState<'subject' | 'body'>('body');
    const [assignmentActiveField, setAssignmentActiveField] = useState<'subject' | 'body'>('body');
    const [confirmationActiveField, setConfirmationActiveField] = useState<'subject' | 'body'>('body');
    const [invoicePaidActiveField, setInvoicePaidActiveField] = useState<'subject' | 'body'>('body');
    const [invoicePartialActiveField, setInvoicePartialActiveField] = useState<'subject' | 'body'>('body');
    const [invoiceUnpaidActiveField, setInvoiceUnpaidActiveField] = useState<'subject' | 'body'>('body');
    const employeeSubjectRef = useRef<HTMLInputElement>(null);
    const employeeBodyRef = useRef<HTMLTextAreaElement>(null);
    const clientSubjectRef = useRef<HTMLInputElement>(null);
    const clientBodyRef = useRef<HTMLTextAreaElement>(null);
    const assignmentSubjectRef = useRef<HTMLInputElement>(null);
    const assignmentBodyRef = useRef<HTMLTextAreaElement>(null);
    const confirmationSubjectRef = useRef<HTMLInputElement>(null);
    const confirmationBodyRef = useRef<HTMLTextAreaElement>(null);
    const invoicePaidSubjectRef = useRef<HTMLInputElement>(null);
    const invoicePaidBodyRef = useRef<HTMLTextAreaElement>(null);
    const invoicePartialSubjectRef = useRef<HTMLInputElement>(null);
    const invoicePartialBodyRef = useRef<HTMLTextAreaElement>(null);
    const invoiceUnpaidSubjectRef = useRef<HTMLInputElement>(null);
    const invoiceUnpaidBodyRef = useRef<HTMLTextAreaElement>(null);
    const [bookingLinkActiveField, setBookingLinkActiveField] = useState<'subject' | 'body'>('body');
    const bookingLinkSubjectRef = useRef<HTMLInputElement>(null);
    const bookingLinkBodyRef = useRef<HTMLTextAreaElement>(null);
    const [newRequestActiveField, setNewRequestActiveField] = useState<'subject' | 'body'>('body');
    const newRequestSubjectRef = useRef<HTMLInputElement>(null);
    const newRequestBodyRef = useRef<HTMLTextAreaElement>(null);
    const [docResponseActiveField, setDocResponseActiveField] = useState<'subject' | 'body'>('body');
    const docResponseSubjectRef = useRef<HTMLInputElement>(null);
    const docResponseBodyRef = useRef<HTMLTextAreaElement>(null);
    const [docPaymentActiveField, setDocPaymentActiveField] = useState<'subject' | 'body'>('body');
    const docPaymentSubjectRef = useRef<HTMLInputElement>(null);
    const docPaymentBodyRef = useRef<HTMLTextAreaElement>(null);
    const [absenceEmployerActiveField, setAbsenceEmployerActiveField] = useState<'subject' | 'body'>('body');
    const absenceEmployerSubjectRef = useRef<HTMLInputElement>(null);
    const absenceEmployerBodyRef = useRef<HTMLTextAreaElement>(null);
    const [devisActiveField, setDevisActiveField] = useState<'subject' | 'body'>('body');
    // Relance du devis (mail distinct, 2026-09-21). Le texte du bouton n'est pas édité ici mais dans la
    // modale du module Devis : on le recharge et on le renvoie tel quel, sinon l'enregistrement l'effaçait.
    // Refus motivé d'une demande de devis (2026-09-24).
    const [demandeRefuseeSubject, setDemandeRefuseeSubject] = useState('');
    const [demandeRefuseeBody, setDemandeRefuseeBody] = useState('');
    const [demandeRefuseeActiveField, setDemandeRefuseeActiveField] = useState<'subject' | 'body'>('body');
    const demandeRefuseeSubjectRef = useRef<HTMLInputElement>(null);
    const demandeRefuseeBodyRef = useRef<HTMLTextAreaElement>(null);
    const [devisRelanceSubject, setDevisRelanceSubject] = useState('');
    const [devisRelanceBody, setDevisRelanceBody] = useState('');
    const [devisRelanceActiveField, setDevisRelanceActiveField] = useState<'subject' | 'body'>('body');
    const [devisButtonLabel, setDevisButtonLabel] = useState('');
    // Invitation à réserver son créneau (lien public, lot B 2026-09-22).
    const [bookingInviteSubject, setBookingInviteSubject] = useState('');
    const [bookingInviteBody, setBookingInviteBody] = useState('');
    const [bookingInviteButtonLabel, setBookingInviteButtonLabel] = useState('');
    const [bookingInviteActiveField, setBookingInviteActiveField] = useState<'subject' | 'body'>('body');
    const bookingInviteSubjectRef = useRef<HTMLInputElement>(null);
    const bookingInviteBodyRef = useRef<HTMLTextAreaElement>(null);
    // Signature en ligne du devis (lot S1, 2026-09-22) : code à usage unique, puis copie signée au client.
    const [signatureCodeSubject, setSignatureCodeSubject] = useState('');
    const [signatureCodeBody, setSignatureCodeBody] = useState('');
    const [signatureCodeActiveField, setSignatureCodeActiveField] = useState<'subject' | 'body'>('body');
    const signatureCodeSubjectRef = useRef<HTMLInputElement>(null);
    const signatureCodeBodyRef = useRef<HTMLTextAreaElement>(null);
    const [signatureCopySubject, setSignatureCopySubject] = useState('');
    const [signatureCopyBody, setSignatureCopyBody] = useState('');
    const [signatureCopyActiveField, setSignatureCopyActiveField] = useState<'subject' | 'body'>('body');
    const signatureCopySubjectRef = useRef<HTMLInputElement>(null);
    const signatureCopyBodyRef = useRef<HTMLTextAreaElement>(null);
    const devisRelanceSubjectRef = useRef<HTMLInputElement>(null);
    const devisRelanceBodyRef = useRef<HTMLTextAreaElement>(null);
    const devisSubjectRef = useRef<HTMLInputElement>(null);
    const devisBodyRef = useRef<HTMLTextAreaElement>(null);
    // Démarchage à domicile (2026-10-21) : e-mail juste après la signature chez le client, puis confirmation de sa
    // demande d'intervention avant la fin du délai de rétractation. Enregistrés par la section « demarchage ».
    const [notifyVisite, setNotifyVisite] = useState(true);
    const [visiteSubject, setVisiteSubject] = useState('');
    const [visiteBody, setVisiteBody] = useState('');
    const [visiteButtonLabel, setVisiteButtonLabel] = useState('');
    const [visiteActiveField, setVisiteActiveField] = useState<'subject' | 'body'>('body');
    const visiteSubjectRef = useRef<HTMLInputElement>(null);
    const visiteBodyRef = useRef<HTMLTextAreaElement>(null);
    const [anticipationSubject, setAnticipationSubject] = useState('');
    const [anticipationBody, setAnticipationBody] = useState('');
    const [anticipationActiveField, setAnticipationActiveField] = useState<'subject' | 'body'>('body');
    const anticipationSubjectRef = useRef<HTMLInputElement>(null);
    const anticipationBodyRef = useRef<HTMLTextAreaElement>(null);
    const insertVisiteToken = (token: string) => {
        if (visiteActiveField === 'subject') insertNotificationToken(visiteSubjectRef.current, visiteSubject, setVisiteSubject, token);
        else insertNotificationToken(visiteBodyRef.current, visiteBody, setVisiteBody, token);
    };
    const insertAnticipationToken = (token: string) => {
        if (anticipationActiveField === 'subject') insertNotificationToken(anticipationSubjectRef.current, anticipationSubject, setAnticipationSubject, token);
        else insertNotificationToken(anticipationBodyRef.current, anticipationBody, setAnticipationBody, token);
    };

    const insertEmployeeToken = (token: string) => {
        if (employeeActiveField === 'subject') insertNotificationToken(employeeSubjectRef.current, employeeSubject, setEmployeeSubject, token);
        else insertNotificationToken(employeeBodyRef.current, employeeBody, setEmployeeBody, token);
    };
    const insertClientToken = (token: string) => {
        if (clientActiveField === 'subject') insertNotificationToken(clientSubjectRef.current, clientSubject, setClientSubject, token);
        else insertNotificationToken(clientBodyRef.current, clientBody, setClientBody, token);
    };
    const insertAssignmentToken = (token: string) => {
        if (assignmentActiveField === 'subject') insertNotificationToken(assignmentSubjectRef.current, assignmentSubject, setAssignmentSubject, token);
        else insertNotificationToken(assignmentBodyRef.current, assignmentBody, setAssignmentBody, token);
    };
    const insertConfirmationToken = (token: string) => {
        if (confirmationActiveField === 'subject') insertNotificationToken(confirmationSubjectRef.current, confirmationSubject, setConfirmationSubject, token);
        else insertNotificationToken(confirmationBodyRef.current, confirmationBody, setConfirmationBody, token);
    };
    const insertInvoicePaidToken = (token: string) => {
        if (invoicePaidActiveField === 'subject') insertNotificationToken(invoicePaidSubjectRef.current, invoicePaidSubject, setInvoicePaidSubject, token);
        else insertNotificationToken(invoicePaidBodyRef.current, invoicePaidBody, setInvoicePaidBody, token);
    };
    const insertInvoicePartialToken = (token: string) => {
        if (invoicePartialActiveField === 'subject') insertNotificationToken(invoicePartialSubjectRef.current, invoicePartialSubject, setInvoicePartialSubject, token);
        else insertNotificationToken(invoicePartialBodyRef.current, invoicePartialBody, setInvoicePartialBody, token);
    };
    const insertInvoiceUnpaidToken = (token: string) => {
        if (invoiceUnpaidActiveField === 'subject') insertNotificationToken(invoiceUnpaidSubjectRef.current, invoiceUnpaidSubject, setInvoiceUnpaidSubject, token);
        else insertNotificationToken(invoiceUnpaidBodyRef.current, invoiceUnpaidBody, setInvoiceUnpaidBody, token);
    };
    const insertBookingLinkToken = (token: string) => {
        if (bookingLinkActiveField === 'subject') insertNotificationToken(bookingLinkSubjectRef.current, bookingLinkSubject, setBookingLinkSubject, token);
        else insertNotificationToken(bookingLinkBodyRef.current, bookingLinkBody, setBookingLinkBody, token);
    };
    const insertNewRequestToken = (token: string) => {
        if (newRequestActiveField === 'subject') insertNotificationToken(newRequestSubjectRef.current, newRequestSubject, setNewRequestSubject, token);
        else insertNotificationToken(newRequestBodyRef.current, newRequestBody, setNewRequestBody, token);
    };
    const insertDocResponseToken = (token: string) => {
        if (docResponseActiveField === 'subject') insertNotificationToken(docResponseSubjectRef.current, docResponseSubject, setDocResponseSubject, token);
        else insertNotificationToken(docResponseBodyRef.current, docResponseBody, setDocResponseBody, token);
    };
    const insertDocPaymentToken = (token: string) => {
        if (docPaymentActiveField === 'subject') insertNotificationToken(docPaymentSubjectRef.current, docPaymentSubject, setDocPaymentSubject, token);
        else insertNotificationToken(docPaymentBodyRef.current, docPaymentBody, setDocPaymentBody, token);
    };
    const insertAbsenceEmployerToken = (token: string) => {
        if (absenceEmployerActiveField === 'subject') insertNotificationToken(absenceEmployerSubjectRef.current, absenceEmployerSubject, setAbsenceEmployerSubject, token);
        else insertNotificationToken(absenceEmployerBodyRef.current, absenceEmployerBody, setAbsenceEmployerBody, token);
    };
    const insertDevisToken = (token: string) => {
        if (devisActiveField === 'subject') insertNotificationToken(devisSubjectRef.current, devisSubject, setDevisSubject, token);
        else insertNotificationToken(devisBodyRef.current, devisBody, setDevisBody, token);
    };
    const insertBookingInviteToken = (token: string) => {
        if (bookingInviteActiveField === 'subject') insertNotificationToken(bookingInviteSubjectRef.current, bookingInviteSubject, setBookingInviteSubject, token);
        else insertNotificationToken(bookingInviteBodyRef.current, bookingInviteBody, setBookingInviteBody, token);
    };
    const insertSignatureCodeToken = (token: string) => {
        if (signatureCodeActiveField === 'subject') insertNotificationToken(signatureCodeSubjectRef.current, signatureCodeSubject, setSignatureCodeSubject, token);
        else insertNotificationToken(signatureCodeBodyRef.current, signatureCodeBody, setSignatureCodeBody, token);
    };
    const insertSignatureCopyToken = (token: string) => {
        if (signatureCopyActiveField === 'subject') insertNotificationToken(signatureCopySubjectRef.current, signatureCopySubject, setSignatureCopySubject, token);
        else insertNotificationToken(signatureCopyBodyRef.current, signatureCopyBody, setSignatureCopyBody, token);
    };
    const wrapSignatureCodeBody = (marker: string) => wrapNotificationSelection(signatureCodeBodyRef.current, signatureCodeBody, setSignatureCodeBody, marker);
    const wrapSignatureCopyBody = (marker: string) => wrapNotificationSelection(signatureCopyBodyRef.current, signatureCopyBody, setSignatureCopyBody, marker);
    const wrapBookingInviteBody = (marker: string) => wrapNotificationSelection(bookingInviteBodyRef.current, bookingInviteBody, setBookingInviteBody, marker);
    const insertDemandeRefuseeToken = (token: string) => {
        if (demandeRefuseeActiveField === 'subject') insertNotificationToken(demandeRefuseeSubjectRef.current, demandeRefuseeSubject, setDemandeRefuseeSubject, token);
        else insertNotificationToken(demandeRefuseeBodyRef.current, demandeRefuseeBody, setDemandeRefuseeBody, token);
    };
    const wrapDemandeRefuseeBody = (marker: string) => wrapNotificationSelection(demandeRefuseeBodyRef.current, demandeRefuseeBody, setDemandeRefuseeBody, marker);
    const insertDevisRelanceToken = (token: string) => {
        if (devisRelanceActiveField === 'subject') insertNotificationToken(devisRelanceSubjectRef.current, devisRelanceSubject, setDevisRelanceSubject, token);
        else insertNotificationToken(devisRelanceBodyRef.current, devisRelanceBody, setDevisRelanceBody, token);
    };

    const wrapEmployeeBody = (marker: string) => wrapNotificationSelection(employeeBodyRef.current, employeeBody, setEmployeeBody, marker);
    const wrapClientBody = (marker: string) => wrapNotificationSelection(clientBodyRef.current, clientBody, setClientBody, marker);
    const wrapAssignmentBody = (marker: string) => wrapNotificationSelection(assignmentBodyRef.current, assignmentBody, setAssignmentBody, marker);
    const wrapConfirmationBody = (marker: string) => wrapNotificationSelection(confirmationBodyRef.current, confirmationBody, setConfirmationBody, marker);
    const wrapInvoicePaidBody = (marker: string) => wrapNotificationSelection(invoicePaidBodyRef.current, invoicePaidBody, setInvoicePaidBody, marker);
    const wrapInvoicePartialBody = (marker: string) => wrapNotificationSelection(invoicePartialBodyRef.current, invoicePartialBody, setInvoicePartialBody, marker);
    const wrapInvoiceUnpaidBody = (marker: string) => wrapNotificationSelection(invoiceUnpaidBodyRef.current, invoiceUnpaidBody, setInvoiceUnpaidBody, marker);
    const wrapBookingLinkBody = (marker: string) => wrapNotificationSelection(bookingLinkBodyRef.current, bookingLinkBody, setBookingLinkBody, marker);
    const wrapNewRequestBody = (marker: string) => wrapNotificationSelection(newRequestBodyRef.current, newRequestBody, setNewRequestBody, marker);
    const wrapDocResponseBody = (marker: string) => wrapNotificationSelection(docResponseBodyRef.current, docResponseBody, setDocResponseBody, marker);
    const wrapDocPaymentBody = (marker: string) => wrapNotificationSelection(docPaymentBodyRef.current, docPaymentBody, setDocPaymentBody, marker);
    const wrapAbsenceEmployerBody = (marker: string) => wrapNotificationSelection(absenceEmployerBodyRef.current, absenceEmployerBody, setAbsenceEmployerBody, marker);
    const wrapDevisBody = (marker: string) => wrapNotificationSelection(devisBodyRef.current, devisBody, setDevisBody, marker);
    const wrapDevisRelanceBody = (marker: string) => wrapNotificationSelection(devisRelanceBodyRef.current, devisRelanceBody, setDevisRelanceBody, marker);

    useEffect(() => {
        moduleApi.getPrestationNotificationSettings().then((s: any) => {
            setNotifyEmployee(s?.notify_employee !== false);
            setNotifyClient(s?.notify_client !== false);
            setNotifyAssignment(s?.notify_assignment !== false);
            setNotifyConfirmation(s?.notify_confirmation !== false);
            setNotifyInvoice(s?.notify_invoice !== false);
            setEmployeeSubject(s?.employee_subject || '');
            setEmployeeBody(s?.employee_body || '');
            setClientSubject(s?.client_subject || '');
            setClientBody(s?.client_body || '');
            setAssignmentSubject(s?.assignment_subject || '');
            setAssignmentBody(s?.assignment_body || '');
            setConfirmationSubject(s?.confirmation_subject || '');
            setConfirmationBody(s?.confirmation_body || '');
            setInvoicePaidSubject(s?.invoice_paid_subject || '');
            setInvoicePaidBody(s?.invoice_paid_body || '');
            setInvoicePartialSubject(s?.invoice_partial_subject || '');
            setInvoicePartialBody(s?.invoice_partial_body || '');
            setInvoiceUnpaidSubject(s?.invoice_unpaid_subject || '');
            setInvoiceUnpaidBody(s?.invoice_unpaid_body || '');
            setBookingLinkSubject(s?.booking_link_subject || '');
            setBookingLinkBody(s?.booking_link_body || '');
            setNewRequestSubject(s?.new_request_subject || '');
            setNewRequestBody(s?.new_request_body || '');
            setDocResponseSubject(s?.doc_response_subject || '');
            setDocResponseBody(s?.doc_response_body || '');
            setDocPaymentSubject(s?.doc_payment_subject || '');
            setDocPaymentBody(s?.doc_payment_body || '');
            setNotifyAbsenceEmployer(s?.notify_absence_employer !== false);
            setAbsenceEmployerSubject(s?.absence_employer_subject || '');
            setAbsenceEmployerBody(s?.absence_employer_body || '');
            setDevisSubject(s?.devis_subject || '');
            setDevisBody(s?.devis_body || '');
            setDemandeRefuseeSubject(s?.demande_refusee_subject || '');
            setDemandeRefuseeBody(s?.demande_refusee_body || '');
            setDevisRelanceSubject(s?.devis_relance_subject || '');
            setDevisRelanceBody(s?.devis_relance_body || '');
            setDevisButtonLabel(s?.devis_button_label || '');
            setBookingInviteSubject(s?.booking_invite_subject || '');
            setBookingInviteBody(s?.booking_invite_body || '');
            setBookingInviteButtonLabel(s?.booking_invite_button_label || '');
            setSignatureCodeSubject(s?.signature_code_subject || '');
            setSignatureCodeBody(s?.signature_code_body || '');
            setSignatureCopySubject(s?.signature_copy_subject || '');
            setSignatureCopyBody(s?.signature_copy_body || '');
            setNotifyVisite(s?.notify_demarchage_visite !== false);
            setVisiteSubject(s?.demarchage_visite_subject || '');
            setVisiteBody(s?.demarchage_visite_body || '');
            setVisiteButtonLabel(s?.demarchage_visite_button_label || '');
            setAnticipationSubject(s?.demarchage_anticipation_subject || '');
            setAnticipationBody(s?.demarchage_anticipation_body || '');
            setAttachments(s?.attachments || {});
        }).catch((e) => console.error('Failed to load notification settings', e)).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        moduleApi.getFinPrestationEmail().then((r: any) => {
            if (!r || r.error) return;
            setFinActif(r.closure_enabled !== false);
            setFinSujet(r.closure_subject || '');
            setFinCorps(r.closure_body || '');
            setFinPayeSujet(r.closure_paid_subject || '');
            setFinPayeCorps(r.closure_paid_body || '');
        }).catch(() => {});
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setEchecEnregistrement(false);
        try {
            const result: any = await moduleApi.savePrestationNotificationSettings({
                notify_employee: notifyEmployee, notify_client: notifyClient,
                notify_assignment: notifyAssignment, notify_confirmation: notifyConfirmation, notify_invoice: notifyInvoice,
                notify_absence_employer: notifyAbsenceEmployer,
                employee_subject: employeeSubject, employee_body: employeeBody,
                client_subject: clientSubject, client_body: clientBody,
                assignment_subject: assignmentSubject, assignment_body: assignmentBody,
                confirmation_subject: confirmationSubject, confirmation_body: confirmationBody,
                invoice_paid_subject: invoicePaidSubject, invoice_paid_body: invoicePaidBody,
                invoice_partial_subject: invoicePartialSubject, invoice_partial_body: invoicePartialBody,
                invoice_unpaid_subject: invoiceUnpaidSubject, invoice_unpaid_body: invoiceUnpaidBody,
                booking_link_subject: bookingLinkSubject, booking_link_body: bookingLinkBody,
                new_request_subject: newRequestSubject, new_request_body: newRequestBody,
                doc_response_subject: docResponseSubject, doc_response_body: docResponseBody,
                doc_payment_subject: docPaymentSubject, doc_payment_body: docPaymentBody,
                absence_employer_subject: absenceEmployerSubject, absence_employer_body: absenceEmployerBody,
                devis_subject: devisSubject, devis_body: devisBody, devis_button_label: devisButtonLabel,
                devis_relance_subject: devisRelanceSubject, devis_relance_body: devisRelanceBody,
                demande_refusee_subject: demandeRefuseeSubject, demande_refusee_body: demandeRefuseeBody,
                booking_invite_subject: bookingInviteSubject, booking_invite_body: bookingInviteBody, booking_invite_button_label: bookingInviteButtonLabel,
                signature_code_subject: signatureCodeSubject, signature_code_body: signatureCodeBody,
                signature_copy_subject: signatureCopySubject, signature_copy_body: signatureCopyBody,
                attachments,
            });
            // Mail de fin de prestation : même bouton, route dédiée. Une erreur ici remonte comme les autres.
            const fin: any = await moduleApi.saveFinPrestationEmail({ closure_enabled: finActif, closure_subject: finSujet, closure_body: finCorps, closure_paid_subject: finPayeSujet, closure_paid_body: finPayeCorps });
            if (fin?.error) throw new Error(fin.error);
            // Démarchage à domicile : section à part, même bouton.
            const dem: any = await moduleApi.patchPrestationNotificationSettings('demarchage', {
                notify_demarchage_visite: notifyVisite, demarchage_visite_subject: visiteSubject, demarchage_visite_body: visiteBody,
                demarchage_visite_button_label: visiteButtonLabel, demarchage_anticipation_subject: anticipationSubject, demarchage_anticipation_body: anticipationBody,
            });
            if (dem?.error) throw new Error(dem.error);
            if (result) {
                setAttachments(result.attachments || {});
                setNotifyAssignment(result.notify_assignment !== false);
                setNotifyConfirmation(result.notify_confirmation !== false);
                setNotifyInvoice(result.notify_invoice !== false);
                setEmployeeSubject(result.employee_subject || '');
                setEmployeeBody(result.employee_body || '');
                setClientSubject(result.client_subject || '');
                setClientBody(result.client_body || '');
                setAssignmentSubject(result.assignment_subject || '');
                setAssignmentBody(result.assignment_body || '');
                setConfirmationSubject(result.confirmation_subject || '');
                setConfirmationBody(result.confirmation_body || '');
                setInvoicePaidSubject(result.invoice_paid_subject || '');
                setInvoicePaidBody(result.invoice_paid_body || '');
                setInvoicePartialSubject(result.invoice_partial_subject || '');
                setInvoicePartialBody(result.invoice_partial_body || '');
                setInvoiceUnpaidSubject(result.invoice_unpaid_subject || '');
                setInvoiceUnpaidBody(result.invoice_unpaid_body || '');
                setBookingLinkSubject(result.booking_link_subject || '');
                setBookingLinkBody(result.booking_link_body || '');
                setNewRequestSubject(result.new_request_subject || '');
                setNewRequestBody(result.new_request_body || '');
                setDocResponseSubject(result.doc_response_subject || '');
                setDocResponseBody(result.doc_response_body || '');
                setDocPaymentSubject(result.doc_payment_subject || '');
                setDocPaymentBody(result.doc_payment_body || '');
                setNotifyAbsenceEmployer(result.notify_absence_employer !== false);
                setAbsenceEmployerSubject(result.absence_employer_subject || '');
                setAbsenceEmployerBody(result.absence_employer_body || '');
                setDevisSubject(result.devis_subject || '');
                setDevisBody(result.devis_body || '');
            }
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save notification settings', e);
            setEchecEnregistrement(true);
        } finally {
            setSaving(false);
        }
    };

    const tabButton = (active: boolean) =>
        `flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${active ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`;

    // Props communes à chaque email : évite de recopier dix lignes par bloc.
    const bloc = (
        valeurs: { subject: string; setSubject: (v: string) => void; body: string; setBody: (v: string) => void },
        refs: { subjectRef: React.RefObject<HTMLInputElement>; bodyRef: React.RefObject<HTMLTextAreaElement> },
        champ: { activeField: 'subject' | 'body'; setActiveField: (f: 'subject' | 'body') => void },
        onInsertToken: (t: string) => void,
    ) => ({ ...valeurs, ...refs, ...champ, onInsertToken });

    const contenu = loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
    ) : recipient === 'salarie' ? (
        <GroupeNotifications titre="Rendez-vous des salariés" icone={<UsersIcon size={13} />}>
            <NotificationBlock title="Rappel de rendez-vous au salarié"
                description="Part quand vous cliquez sur « Envoyer un rappel » sur la fiche du rendez-vous."
                toggle={{ checked: notifyEmployee, onChange: setNotifyEmployee }}
                {...bloc({ subject: employeeSubject, setSubject: setEmployeeSubject, body: employeeBody, setBody: setEmployeeBody },
                    { subjectRef: employeeSubjectRef, bodyRef: employeeBodyRef },
                    { activeField: employeeActiveField, setActiveField: setEmployeeActiveField }, insertEmployeeToken)}
                attachment={attachments.employee} onAttachmentChange={(att) => setAttachment('employee', att)} />
            <NotificationBlock title="Nouveau rendez-vous assigné"
                description="Part automatiquement dès qu'un salarié est assigné à un rendez-vous."
                toggle={{ checked: notifyAssignment, onChange: setNotifyAssignment }}
                {...bloc({ subject: assignmentSubject, setSubject: setAssignmentSubject, body: assignmentBody, setBody: setAssignmentBody },
                    { subjectRef: assignmentSubjectRef, bodyRef: assignmentBodyRef },
                    { activeField: assignmentActiveField, setActiveField: setAssignmentActiveField }, insertAssignmentToken)}
                attachment={attachments.assignment} onAttachmentChange={(att) => setAttachment('assignment', att)} />
        </GroupeNotifications>
    ) : recipient === 'etablissement' ? (
        <>
            <GroupeNotifications titre="Demandes reçues" icone={<Building2 size={13} />}>
                <NotificationBlock title="Nouvelle demande de rendez-vous"
                    description="Part dès qu'un client demande un rendez-vous (Démarche client ou lien de réservation). Il reste en attente tant qu'aucun salarié n'est assigné."
                    {...bloc({ subject: newRequestSubject, setSubject: setNewRequestSubject, body: newRequestBody, setBody: setNewRequestBody },
                        { subjectRef: newRequestSubjectRef, bodyRef: newRequestBodyRef },
                        { activeField: newRequestActiveField, setActiveField: setNewRequestActiveField }, insertNewRequestToken)}
                    attachment={attachments.new_request} onAttachmentChange={(att) => setAttachment('new_request', att)} />
                {showDemarches && (
                    <NotificationBlock title="Nouvelle réponse à une Démarche client"
                        description="Part dès qu'un client répond à une Démarche que vous lui avez envoyée (formulaire, choix, paiement)."
                        {...bloc({ subject: docResponseSubject, setSubject: setDocResponseSubject, body: docResponseBody, setBody: setDocResponseBody },
                            { subjectRef: docResponseSubjectRef, bodyRef: docResponseBodyRef },
                            { activeField: docResponseActiveField, setActiveField: setDocResponseActiveField }, insertDocResponseToken)}
                        attachment={attachments.doc_response} onAttachmentChange={(att) => setAttachment('doc_response', att)} />
                )}
                {showDemarches && (
                    <NotificationBlock title="Paiement reçu (Démarche client)"
                        description="Part dès qu'un paiement lié à une Démarche client est marqué comme reçu."
                        {...bloc({ subject: docPaymentSubject, setSubject: setDocPaymentSubject, body: docPaymentBody, setBody: setDocPaymentBody },
                            { subjectRef: docPaymentSubjectRef, bodyRef: docPaymentBodyRef },
                            { activeField: docPaymentActiveField, setActiveField: setDocPaymentActiveField }, insertDocPaymentToken)}
                        attachment={attachments.doc_payment} onAttachmentChange={(att) => setAttachment('doc_payment', att)} />
                )}
                {showConges && (
                    <NotificationBlock title="Nouvelle demande d'absence"
                        description="Part dès qu'un salarié envoie une demande d'absence ou de congé."
                        toggle={{ checked: notifyAbsenceEmployer, onChange: setNotifyAbsenceEmployer }}
                        {...bloc({ subject: absenceEmployerSubject, setSubject: setAbsenceEmployerSubject, body: absenceEmployerBody, setBody: setAbsenceEmployerBody },
                            { subjectRef: absenceEmployerSubjectRef, bodyRef: absenceEmployerBodyRef },
                            { activeField: absenceEmployerActiveField, setActiveField: setAbsenceEmployerActiveField }, insertAbsenceEmployerToken)}
                        attachment={attachments.absence_employer} onAttachmentChange={(att) => setAttachment('absence_employer', att)} />
                )}
            </GroupeNotifications>
        </>
    ) : (
        <>
            <GroupeNotifications titre="Rendez-vous" icone={<User size={13} />}>
                <NotificationBlock title="Confirmation de rendez-vous"
                    description="Part automatiquement dès qu'un rendez-vous est saisi."
                    toggle={{ checked: notifyConfirmation, onChange: setNotifyConfirmation }}
                    {...bloc({ subject: confirmationSubject, setSubject: setConfirmationSubject, body: confirmationBody, setBody: setConfirmationBody },
                        { subjectRef: confirmationSubjectRef, bodyRef: confirmationBodyRef },
                        { activeField: confirmationActiveField, setActiveField: setConfirmationActiveField }, insertConfirmationToken)}
                    tokens={[...ALL_NOTIFICATION_TOKENS, { token: '{{lien_annulation}}', label: 'Bouton "Annuler le rendez-vous" (cliquez où l\'insérer)' }]}
                    attachment={attachments.confirmation} onAttachmentChange={(att) => setAttachment('confirmation', att)} />
                <NotificationBlock title="Rappel de rendez-vous"
                    description="Part quand vous cliquez sur « Envoyer un rappel » sur la fiche du rendez-vous."
                    toggle={{ checked: notifyClient, onChange: setNotifyClient }}
                    {...bloc({ subject: clientSubject, setSubject: setClientSubject, body: clientBody, setBody: setClientBody },
                        { subjectRef: clientSubjectRef, bodyRef: clientBodyRef },
                        { activeField: clientActiveField, setActiveField: setClientActiveField }, insertClientToken)}
                    attachment={attachments.client} onAttachmentChange={(att) => setAttachment('client', att)} />
                <NotificationBlock title="Invitation à réserver en ligne"
                    description="Part quand vous cliquez sur « Envoyer un lien de rendez-vous » dans le Planning : le client choisit lui-même son créneau."
                    {...bloc({ subject: bookingLinkSubject, setSubject: setBookingLinkSubject, body: bookingLinkBody, setBody: setBookingLinkBody },
                        { subjectRef: bookingLinkSubjectRef, bodyRef: bookingLinkBodyRef },
                        { activeField: bookingLinkActiveField, setActiveField: setBookingLinkActiveField }, insertBookingLinkToken)}
                    attachment={attachments.booking_link} onAttachmentChange={(att) => setAttachment('booking_link', att)} />
            </GroupeNotifications>

            <GroupeNotifications titre="Fin de prestation et règlement" icone={<Receipt size={13} />}>
                <FinPrestationEmail actif={finActif} setActif={setFinActif} sujet={finSujet} setSujet={setFinSujet} corps={finCorps} setCorps={setFinCorps} />
                <FinPrestationEmail paye actif={finActif} setActif={setFinActif} sujet={finPayeSujet} setSujet={setFinPayeSujet} corps={finPayeCorps} setCorps={setFinPayeCorps}
                    extra={enLignes ? <JoindreFactureToggle champ="closure_paid_attach_invoice" texte="Coché, la facture soldée est jointe à ce mail." /> : undefined} />
                <NotificationBlock title="Merci pour votre règlement"
                    description={`Part dès que la prestation est payée en intégralité. Tant qu'il reste un solde, aucune facture ne part.${showFacture ? '' : ' Module Facture inactif : ce mail part sans facture PDF.'}`}
                    toggle={{ checked: notifyInvoice, onChange: setNotifyInvoice }}
                    {...bloc({ subject: invoicePaidSubject, setSubject: setInvoicePaidSubject, body: invoicePaidBody, setBody: setInvoicePaidBody },
                        { subjectRef: invoicePaidSubjectRef, bodyRef: invoicePaidBodyRef },
                        { activeField: invoicePaidActiveField, setActiveField: setInvoicePaidActiveField }, insertInvoicePaidToken)}
                    tokens={[...ALL_NOTIFICATION_TOKENS, { token: '{{lien_avis}}', label: 'Bouton « Donner mon avis » (module Avis)' }]}
                    attachment={attachments.invoice_paid} onAttachmentChange={(att) => setAttachment('invoice_paid', att)}
                    extra={enLignes ? <JoindreFactureToggle /> : undefined} />
            </GroupeNotifications>
            {/* Mode classique (fenêtre du module Planning) : le réglage de la pièce jointe reste sous le bloc. */}
            {!enLignes && <JoindreFactureToggle />}
            {!enLignes && <JoindreFactureToggle champ="closure_paid_attach_invoice" texte="Mail « fin de prestation déjà réglée » : coché, la facture soldée y est jointe." />}

            <GroupeNotifications titre="Devis et signature">
                <NotificationBlock title="Envoi du devis"
                    description="Part quand vous cliquez sur « Envoyer le devis »."
                    {...bloc({ subject: devisSubject, setSubject: setDevisSubject, body: devisBody, setBody: setDevisBody },
                        { subjectRef: devisSubjectRef, bodyRef: devisBodyRef },
                        { activeField: devisActiveField, setActiveField: setDevisActiveField }, insertDevisToken)}
                    attachment={attachments.devis} onAttachmentChange={(att) => setAttachment('devis', att)} />
                <NotificationBlock title="Relance du devis"
                    description="Part quand vous cliquez sur « Relancer le client » sur un dossier resté à « Devis envoyé » (sans le PDF : le lien de signature suffit)."
                    {...bloc({ subject: devisRelanceSubject, setSubject: setDevisRelanceSubject, body: devisRelanceBody, setBody: setDevisRelanceBody },
                        { subjectRef: devisRelanceSubjectRef, bodyRef: devisRelanceBodyRef },
                        { activeField: devisRelanceActiveField, setActiveField: setDevisRelanceActiveField }, insertDevisRelanceToken)} />
                <NotificationBlock title="Refus d'une demande"
                    description="Part quand vous refusez une demande en cochant « Prévenir le client ». La variable « Motif » reprend la phrase que vous écrivez au moment du refus."
                    {...bloc({ subject: demandeRefuseeSubject, setSubject: setDemandeRefuseeSubject, body: demandeRefuseeBody, setBody: setDemandeRefuseeBody },
                        { subjectRef: demandeRefuseeSubjectRef, bodyRef: demandeRefuseeBodyRef },
                        { activeField: demandeRefuseeActiveField, setActiveField: setDemandeRefuseeActiveField }, insertDemandeRefuseeToken)} />
                <NotificationBlock title="Lien de réservation après signature"
                    description="Part quand vous envoyez le lien de réservation sur un dossier à « Devis signé » : le client choisit son créneau parmi vos disponibilités."
                    {...bloc({ subject: bookingInviteSubject, setSubject: setBookingInviteSubject, body: bookingInviteBody, setBody: setBookingInviteBody },
                        { subjectRef: bookingInviteSubjectRef, bodyRef: bookingInviteBodyRef },
                        { activeField: bookingInviteActiveField, setActiveField: setBookingInviteActiveField }, insertBookingInviteToken)}
                    extra={
                        <label className="block">
                            <span className="block text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mb-1.5">Texte du bouton</span>
                            <input className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500"
                                value={bookingInviteButtonLabel} onChange={(e) => setBookingInviteButtonLabel(e.target.value)} placeholder="Choisir mon créneau" />
                        </label>
                    } />
                <NotificationBlock title="Code de signature"
                    description="Le code à 6 chiffres que le client saisit pour signer son devis en ligne."
                    {...bloc({ subject: signatureCodeSubject, setSubject: setSignatureCodeSubject, body: signatureCodeBody, setBody: setSignatureCodeBody },
                        { subjectRef: signatureCodeSubjectRef, bodyRef: signatureCodeBodyRef },
                        { activeField: signatureCodeActiveField, setActiveField: setSignatureCodeActiveField }, insertSignatureCodeToken)}
                    tokens={[...SIGNATURE_TOKENS, { token: '{{code}}', label: 'Code à 6 chiffres (obligatoire)' }]} />
                <NotificationBlock title="Copie du devis signé"
                    description="Part au client après sa signature, avec le PDF scellé et son certificat en pièce jointe."
                    {...bloc({ subject: signatureCopySubject, setSubject: setSignatureCopySubject, body: signatureCopyBody, setBody: setSignatureCopyBody },
                        { subjectRef: signatureCopySubjectRef, bodyRef: signatureCopyBodyRef },
                        { activeField: signatureCopyActiveField, setActiveField: setSignatureCopyActiveField }, insertSignatureCopyToken)}
                    tokens={SIGNATURE_TOKENS} />
            </GroupeNotifications>

            <GroupeNotifications titre="Démarchage à domicile">
                <NotificationBlock title="Après la visite : premier rendez-vous"
                    description="Part juste après la signature d'un devis chez le client (dossier marqué « signé à domicile »), avec le lien pour choisir son premier rendez-vous et ses dates de rétractation."
                    toggle={{ checked: notifyVisite, onChange: setNotifyVisite }}
                    {...bloc({ subject: visiteSubject, setSubject: setVisiteSubject, body: visiteBody, setBody: setVisiteBody },
                        { subjectRef: visiteSubjectRef, bodyRef: visiteBodyRef },
                        { activeField: visiteActiveField, setActiveField: setVisiteActiveField }, insertVisiteToken)}
                    tokens={DEMARCHAGE_VISITE_TOKENS}
                    extra={
                        <label className="block">
                            <span className="block text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mb-1.5">Texte du bouton</span>
                            <input className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500"
                                value={visiteButtonLabel} onChange={(e) => setVisiteButtonLabel(e.target.value)} placeholder="Choisir mon rendez-vous" />
                        </label>
                    } />
                <NotificationBlock title="Demande d'intervention avant la fin de la rétractation" toujours
                    description="Part au client quand il coche, sur la page de réservation, la demande d'intervention avant la fin de son délai de rétractation. Toujours envoyé : il garde la trace de sa demande, dont le texte exact figure toujours dans le message."
                    {...bloc({ subject: anticipationSubject, setSubject: setAnticipationSubject, body: anticipationBody, setBody: setAnticipationBody },
                        { subjectRef: anticipationSubjectRef, bodyRef: anticipationBodyRef },
                        { activeField: anticipationActiveField, setActiveField: setAnticipationActiveField }, insertAnticipationToken)}
                    tokens={DEMARCHAGE_ANTICIPATION_TOKENS} />
            </GroupeNotifications>
        </>
    );

    // Paramètres > Notifications : pas de bouton ni d'onglets ici, la page en porte un seul jeu ; chaque
    // ligne enregistre elle-même (interrupteur ou « Enregistrer » de sa fenêtre).
    if (enLignes) {
        return (
            <FournisseurEnregistrement enregistrer={handleSave}>
                <div className="space-y-6">{contenu}</div>
                <EtatEnregistrement etat={saving ? 'en_cours' : echecEnregistrement ? 'echec' : saved ? 'fait' : 'repos'} />
            </FournisseurEnregistrement>
        );
    }

    return (
        <div className="space-y-6">
            {/* Bouton en ligne (pas flottant) — un bouton fixe à l'écran devenait ambigu dès que cette page
                est montée en même temps que ClientEmailNotificationsSettings (client Prestation avec les
                modules Facture/CRM/Congés aussi actifs) : deux boutons "Enregistrer" apparaissaient alors,
                pour deux formulaires différents, sans qu'on sache lequel enregistrait quoi. */}
            <button onClick={handleSave} disabled={saving}
                className="px-6 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold shadow-lg disabled:opacity-50">
                {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
            </button>

            {!destinataire && (
                <div className="bg-surface border border-border rounded-2xl p-2 inline-flex gap-2">
                    <button onClick={() => setRecipient('client')} className={tabButton(recipient === 'client')}>
                        <User size={14} /> Client
                    </button>
                    <button onClick={() => setRecipient('salarie')} className={tabButton(recipient === 'salarie')}>
                        <UsersIcon size={14} /> Salarié
                    </button>
                    <button onClick={() => setRecipient('etablissement')} className={tabButton(recipient === 'etablissement')}>
                        <Building2 size={14} /> Établissement
                    </button>
                </div>
            )}

            <div className="max-w-3xl space-y-6">{contenu}</div>
        </div>
    );
};

/**
 * Planning › Paramètres › E-mails : la même liste « une ligne par email » que Paramètres > Notifications,
 * avec le choix du destinataire (Client / Salarié / Vous) au-dessus.
 */
export function PrestationNotificationsEnLignes({ activeModules = [] }: { activeModules?: string[] }) {
    const [destinataire, setDestinataire] = useState<'client' | 'salarie' | 'etablissement'>('client');
    return (
        <ModeNotificationsContext.Provider value={{ enLignes: true }}>
            <div className="space-y-4">
                <p className="text-[13px] text-content-muted">
                    L'interrupteur active ou coupe un email ; cliquez sur un titre pour modifier son texte et voir ce que reçoit le destinataire.
                </p>
                <Segmented value={destinataire} ariaLabel="Destinataire des emails" onChange={(v) => setDestinataire(v as typeof destinataire)}
                    options={[{ value: 'client', label: 'Client' }, { value: 'salarie', label: 'Salarié' }, { value: 'etablissement', label: 'Vous' }]} />
                <PrestationNotificationsSettings activeModules={activeModules} destinataire={destinataire} />
            </div>
        </ModeNotificationsContext.Provider>
    );
}
